import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import CustomFormPage from "../../components/common/CustomFormPage";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getQuoteDetails,
    getQuoteMessages,
    postQuoteMessage,
    completeQuote,
} from "../../helpers/http_quotes_requests ";
import { getProducts } from "../../helpers/http_products_requests";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaImage, FaPaperPlane, FaPlus, FaTrash, FaUser } from "react-icons/fa6";
import globalToast from "../../components/common/globalToast";
import { usePermissions } from "../../providers/PermissionsProvider";
import { usePendingQuotes } from "../../providers/PendingQuotesProvider";

const MAX_IMAGES = 3;

function fileToSquareJpeg(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
        reader.onload = (event) => {
            const img = new Image();
            img.onerror = () => reject(new Error("Arquivo de imagem inválido."));
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const size = 500;
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext("2d");
                const aspectRatio = img.width / img.height;
                let drawWidth;
                let drawHeight;
                let offsetX;
                let offsetY;

                if (aspectRatio > 1) {
                    drawWidth = size * aspectRatio;
                    drawHeight = size;
                    offsetX = -(drawWidth - size) / 2;
                    offsetY = 0;
                } else {
                    drawWidth = size;
                    drawHeight = size / aspectRatio;
                    offsetX = 0;
                    offsetY = -(drawHeight - size) / 2;
                }

                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                resolve(canvas.toDataURL("image/jpeg"));
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
}

function MessageBubble({ message, isMine }) {
    const isProposal = message.message_type === "proposal";

    if (isProposal) {
        return (
            <Box sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", gap: 1, alignItems: "flex-end" }}>
                {!isMine && (
                    <Avatar sx={{ width: 32, height: 32 }}>
                        <FaUser size={12} />
                    </Avatar>
                )}
                <Box
                    sx={{
                        maxWidth: 420,
                        width: "100%",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        textAlign: "left",
                    }}
                >
                    <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.6}>
                        PROPOSTA
                    </Typography>
                    <Box display="flex" gap={1.5} mt={1}>
                        {message.product_image && (
                            <Box
                                component="img"
                                src={message.product_image}
                                alt={message.product_name || "Produto"}
                                sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                            />
                        )}
                        <Box flex={1} minWidth={0}>
                            <Typography fontWeight={600}>
                                {message.product_name || `Produto #${message.product_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {message.author_name || "Equipe"}
                                {message.created_at ? `, ${message.created_at}` : ""}
                            </Typography>
                            {message.body ? (
                                <Typography mt={1} sx={{ whiteSpace: "pre-wrap" }} variant="body2">
                                    {message.body}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
                {isMine && (
                    <Avatar src={message.author_image || undefined} sx={{ width: 32, height: 32 }}>
                        <FaUser size={12} />
                    </Avatar>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", gap: 1, alignItems: "flex-end" }}>
            {!isMine && (
                <Avatar sx={{ width: 32, height: 32 }}>
                    <FaUser size={12} />
                </Avatar>
            )}
            <Box
                sx={{
                    maxWidth: 420,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    textAlign: "left",
                    bgcolor: isMine ? "primary.main" : "grey.100",
                    color: isMine ? "primary.contrastText" : "text.primary",
                    borderBottomRightRadius: isMine ? 4 : 16,
                    borderBottomLeftRadius: isMine ? 16 : 4,
                }}
            >
                {!isMine && message.author_name && (
                    <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mb: 0.5 }}>
                        {message.author_name}
                    </Typography>
                )}
                {message.body ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {message.body}
                    </Typography>
                ) : null}
                {Array.isArray(message.images) && message.images.length > 0 && (
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        {message.images.map((img, idx) => (
                            <Box
                                key={`${message.id}-${idx}`}
                                component="img"
                                src={img}
                                alt=""
                                sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1 }}
                            />
                        ))}
                    </Box>
                )}
                <Typography variant="caption" sx={{ display: "block", mt: 0.75, opacity: 0.75 }}>
                    {message.created_at}
                </Typography>
            </Box>
            {isMine && (
                <Avatar src={message.author_image || undefined} sx={{ width: 32, height: 32 }}>
                    <FaUser size={12} />
                </Avatar>
            )}
        </Box>
    );
}

export default function QuoteDetails() {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();
    const { verifyPermission } = usePermissions();
    const { refreshQuotesNotifications } = usePendingQuotes();
    const canEdit = verifyPermission("quotation", 2);

    const [draft, setDraft] = useState("");
    const [draftImages, setDraftImages] = useState([]);
    const [proposalOpen, setProposalOpen] = useState(false);
    const [completeOpen, setCompleteOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [observation, setObservation] = useState("");
    const fileInputRef = useRef(null);
    const threadEndRef = useRef(null);

    const quoteQuery = useQuery({
        queryKey: ["getQuoteDetails", id],
        queryFn: () => getQuoteDetails(id),
    });
    const productsQuery = useQuery({
        queryKey: ["getProducts"],
        queryFn: getProducts,
        staleTime: 1000 * 60,
    });

    const quoteData = quoteQuery?.data?.data?.data;
    const completed = Boolean(quoteData?.completed_at);

    const messagesQuery = useQuery({
        queryKey: ["getQuoteMessages", id],
        queryFn: () => getQuoteMessages(id),
        enabled: Boolean(id),
        refetchInterval: completed ? false : 15000,
        staleTime: 5000,
    });

    const products = useMemo(
        () =>
            productsQuery?.data?.data?.products?.map((item) => ({
                value: item.id,
                label: item.name,
                category: item.category,
                self_reference: item.self_reference,
            })) || [],
        [productsQuery?.data?.data?.products]
    );

    const messages = useMemo(
        () => messagesQuery.data?.data?.data?.messages || [],
        [messagesQuery.data]
    );

    const isFetching = quoteQuery.isFetching;

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["getQuoteMessages", id] });
        queryClient.invalidateQueries({ queryKey: ["getQuoteDetails", id] });
        queryClient.invalidateQueries({ queryKey: ["getQuotes"] });
        refreshQuotesNotifications?.();
    };

    const sendMutation = useMutation({
        mutationFn: postQuoteMessage,
        onSuccess: () => {
            setDraft("");
            setDraftImages([]);
            invalidate();
            globalToast.success("Mensagem enviada.");
        },
        onError: (err) => {
            globalToast.error(err?.response?.data?.message || "Erro ao enviar mensagem.");
        },
    });

    const proposalMutation = useMutation({
        mutationFn: postQuoteMessage,
        onSuccess: () => {
            setProposalOpen(false);
            setSelectedProduct(null);
            setObservation("");
            invalidate();
            globalToast.success("Proposta inserida.");
        },
        onError: (err) => {
            globalToast.error(err?.response?.data?.message || "Erro ao inserir proposta.");
        },
    });

    const completeMutation = useMutation({
        mutationFn: () => completeQuote(id),
        onSuccess: () => {
            setCompleteOpen(false);
            invalidate();
            globalToast.success("Orçamento marcado como concluído.");
        },
        onError: (err) => {
            globalToast.error(err?.response?.data?.message || "Erro ao concluir orçamento.");
        },
    });

    const formFieldList = [
        {
            title: "Dados de Orçamento",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true },
                { name: "customer_name", label: "Nome do Cliente", type: "text", required: true },
                { name: "category_name", label: "Categoria  ", type: "text", required: true },
                { name: "description", label: "Descrição", type: "text", multiline: true, rows: 5, fullWidth: true, required: true },
            ],
        },
    ];

    const handlePickImages = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;
        const remaining = MAX_IMAGES - draftImages.length;
        if (remaining <= 0) {
            globalToast.error(`Máximo de ${MAX_IMAGES} imagens.`);
            return;
        }
        try {
            const converted = await Promise.all(files.slice(0, remaining).map(fileToSquareJpeg));
            setDraftImages((prev) => [...prev, ...converted]);
        } catch (e) {
            globalToast.error("Não foi possível processar a imagem.");
        }
    };

    const handleSend = () => {
        const body = draft.trim();
        if (!body && draftImages.length === 0) return;
        sendMutation.mutate({
            quote_id: Number(id),
            message_type: "text",
            body,
            images: draftImages,
        });
    };

    const handleInsertProposal = () => {
        if (!selectedProduct) {
            globalToast.error("Selecione um produto.");
            return;
        }
        proposalMutation.mutate({
            quote_id: Number(id),
            message_type: "proposal",
            product_id: selectedProduct.value,
            body: observation.trim(),
        });
    };

    return (
        <PageLayout pageTitle={"Orçamento - Detalhes"}>
            <CustomFormPage
                isLoading={isFetching}
                noAction
                details
                multiImageUpload
                initialData={quoteData}
                formFieldList={formFieldList}
                onCancel={() => navigate(-1)}
            />

            <Box my={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h1">Conversa</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    {canEdit && !completed && (
                        <>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<FaPlus />}
                                onClick={() => setProposalOpen(true)}
                            >
                                Inserir proposta
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FaCheck />}
                                onClick={() => setCompleteOpen(true)}
                            >
                                Marcar como concluído
                            </Button>
                        </>
                    )}
                    {completed && (
                        <Typography variant="body2" color="text.secondary" alignSelf="center">
                            Concluído em {quoteData.completed_at}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        maxHeight: 480,
                        overflowY: "auto",
                        mb: 2,
                        minHeight: 120,
                    }}
                >
                    {messagesQuery.isFetching && messages.length === 0 && (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {!messagesQuery.isFetching && messages.length === 0 && (
                        <Typography color="text.secondary" textAlign="left">
                            Nenhuma mensagem ainda.
                        </Typography>
                    )}
                    {messages.map((msg) => (
                        <MessageBubble
                            key={msg.id}
                            message={msg}
                            isMine={msg.author_type === "admin"}
                        />
                    ))}
                    <div ref={threadEndRef} />
                </Box>

                {canEdit && !completed ? (
                    <Box borderTop="1px solid" borderColor="divider" pt={2}>
                        {draftImages.length > 0 && (
                            <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                {draftImages.map((img, idx) => (
                                    <Box key={`d-${idx}`} position="relative">
                                        <Box
                                            component="img"
                                            src={img}
                                            alt=""
                                            sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setDraftImages((prev) => prev.filter((_, i) => i !== idx))
                                            }
                                            sx={{
                                                position: "absolute",
                                                top: -8,
                                                right: -8,
                                                bgcolor: "background.paper",
                                                boxShadow: 1,
                                            }}
                                        >
                                            <FaTrash size={12} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Box display="flex" alignItems="flex-end" gap={0.5}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={handlePickImages}
                            />
                            <IconButton
                                color="primary"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={sendMutation.isPending || draftImages.length >= MAX_IMAGES}
                            >
                                <FaImage />
                            </IconButton>
                            <TextField
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="Escreva uma mensagem…"
                                fullWidth
                                multiline
                                maxRows={4}
                                size="small"
                                variant="filled"
                                disabled={sendMutation.isPending}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />
                            <IconButton
                                color="secondary"
                                onClick={handleSend}
                                disabled={
                                    sendMutation.isPending || (!draft.trim() && draftImages.length === 0)
                                }
                            >
                                {sendMutation.isPending ? <CircularProgress size={20} /> : <FaPaperPlane />}
                            </IconButton>
                        </Box>
                    </Box>
                ) : completed ? (
                    <Typography color="text.secondary" variant="body2" borderTop="1px solid" borderColor="divider" pt={2}>
                        Orçamento concluído. Conversa somente leitura.
                    </Typography>
                ) : null}
            </Box>

            <Dialog open={proposalOpen} onClose={() => setProposalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Inserir proposta</DialogTitle>
                <DialogContent>
                    <Box mt={1} display="flex" flexDirection="column" gap={2}>
                        <Autocomplete
                            options={products}
                            getOptionLabel={(option) =>
                                option?.label
                                    ? `${option.label} - ${option.category} (${option.self_reference})`
                                    : ""
                            }
                            value={selectedProduct}
                            onChange={(_, newValue) => setSelectedProduct(newValue)}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    label="Produto"
                                    required
                                    placeholder="Selecione um produto"
                                />
                            )}
                        />
                        <TextField
                            variant="filled"
                            fullWidth
                            multiline
                            rows={4}
                            label="Observação"
                            placeholder="Mensagem exibida ao cliente junto da proposta."
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProposalOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleInsertProposal}
                        disabled={!selectedProduct || proposalMutation.isPending}
                    >
                        {proposalMutation.isPending ? "Enviando…" : "Inserir proposta"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)}>
                <DialogTitle>Marcar como concluído?</DialogTitle>
                <DialogContent>
                    <Typography>
                        O cliente e a equipe não poderão mais enviar mensagens neste orçamento.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompleteOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => completeMutation.mutate()}
                        disabled={completeMutation.isPending}
                    >
                        {completeMutation.isPending ? "Concluindo…" : "Confirmar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageLayout>
    );
}
